import React from "react";
import { cn } from "@/lib/utils";
import { Star } from "lucide-react";

interface StarRatingFieldProps {
  value: number;
  onChange: (value: number) => void;
  maxStars?: number;
  size?: "sm" | "md" | "lg";
  labels?: string[];
  className?: string;
}

export function StarRatingField({
  value,
  onChange,
  maxStars = 5,
  size = "md",
  labels,
  className,
}: StarRatingFieldProps) {
  const [hoverValue, setHoverValue] = React.useState<number | null>(null);

  const displayValue = hoverValue ?? value;

  const sizeClasses = {
    sm: "h-5 w-5",
    md: "h-8 w-8",
    lg: "h-10 w-10",
  };

  const gapClasses = {
    sm: "gap-1",
    md: "gap-2",
    lg: "gap-3",
  };

  return (
    <div className={cn("flex flex-col items-start", className)}>
      <div className={cn("flex items-center", gapClasses[size])}>
        {Array.from({ length: maxStars }, (_, i) => i + 1).map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHoverValue(star)}
            onMouseLeave={() => setHoverValue(null)}
            className={cn(
              "transition-transform duration-150 hover:scale-110",
              "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
            )}
          >
            <Star
              className={cn(
                sizeClasses[size],
                "transition-colors duration-150",
                star <= displayValue
                  ? "fill-amber-400 text-amber-400"
                  : "fill-transparent text-muted-foreground/40 hover:text-amber-300"
              )}
            />
          </button>
        ))}
      </div>
      
      {labels && labels.length === maxStars && (
        <div className="mt-2 flex w-full justify-between px-1">
          {labels.map((label, index) => (
            <span
              key={index}
              className={cn(
                "text-xs",
                index + 1 === value ? "text-primary font-medium" : "text-muted-foreground"
              )}
            >
              {label}
            </span>
          ))}
        </div>
      )}
      
      {value > 0 && (
        <span className="mt-2 text-sm text-muted-foreground">
          {value} von {maxStars} Sternen
        </span>
      )}
    </div>
  );
}
